import { FC, useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAppDispatch, useAppSelector } from "@/store/app/hooks";
import {
  loadDataSelector,
  includeDeadHeadSelector,
} from "@/store/features/load-info/loadInfoSelectors";
import { isBrokerSelector } from "@/store/features/tabs/tabsSelectors";
import {
  distanceDifferenceSelector,
  tripDataLoadingSelector,
} from "@/store/features/trip-info/tripInfoSelectors";
import {
  setDistance,
  setLoading,
  setDuration,
  setTollCost,
  setDistanceDifference,
  setFuelPrice,
} from "@/store/features/trip-info/tripInfoSlice";
import MarkerIcon from "@/assets/icons/marker.svg";
import LoadMarkerIcon from "@/assets/icons/load-marker.svg";
import TruckMarkerIcon from "@/assets/icons/truck-marker.svg";
import { getRequestDataV2 } from "@/utils/mapUtils";
import { Loader } from "../loader";
import {
  accessTokenSelector,
  refreshTokenSelector,
} from "@/store/features/account/accountSelectors";
import {
  USE_GRAPHHOPPER,
  decodePolyline,
  dieselFromFuel,
  fetchGhRoute,
  pollTolls,
  toRouting8,
} from "@/modules/graphhopper";

interface LatLng {
  lat: number;
  lng: number;
}

interface PricePoint {
  location: LatLng;
  name: string;
  price: number;
}

const priceIcon = (cls: string, text: string) =>
  new L.DivIcon({
    className: cls,
    html: text,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

const FitBounds: FC<{ points: LatLng[] }> = ({ points }) => {
  const map = useMap();
  map.invalidateSize();
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [points, map]);
  return null;
};

const sectionCoords = (section: any): LatLng[] =>
  Array.isArray(section?.coords) && section.coords.length
    ? section.coords
    : decodePolyline(section?.polyline || "");

const pointIcon = new L.Icon({
  iconUrl: MarkerIcon,
  iconSize: [30, 34],
  iconAnchor: [15, 34],
});

const loadIcon = new L.Icon({
  iconUrl: LoadMarkerIcon,
  iconSize: [30, 34],
  iconAnchor: [15, 34],
});

const truckIcon = new L.Icon({
  iconUrl: TruckMarkerIcon,
  iconSize: [30, 34],
  iconAnchor: [15, 34],
});

export const MapNewAPI: FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(tripDataLoadingSelector);

  const isBroker = useAppSelector(isBrokerSelector);
  const loadData = useAppSelector(loadDataSelector);
  const includeDeadHead = useAppSelector(includeDeadHeadSelector);
  const distanceDifference = useAppSelector(distanceDifferenceSelector);
  const accessToken = useAppSelector(accessTokenSelector);
  const refreshToken = useAppSelector(refreshTokenSelector);

  const {
    origin: rawOrigin,
    destination: rawDestination,
    truck_location: truckLocation,
  } = loadData || {};

  const [data, setData] = useState<any>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [polylinePoints, setPolylinePoints] = useState<LatLng[]>([]);
  const [mapOrigin, setMapOrigin] = useState<LatLng | null>(null);
  const [mapDestination, setMapDestination] = useState<LatLng | null>(null);
  const [mapVia, setMapVia] = useState<LatLng | null>(null);
  const [tolls, setTolls] = useState<PricePoint[]>([]);
  const [fuels, setFuels] = useState<PricePoint[]>([]);

  const hasTrack = !!truckLocation && includeDeadHead;
  const startIcon = isBroker || hasTrack ? truckIcon : loadIcon;

  const clearMap = () => {
    setPolylinePoints([]);
    setMapOrigin(null);
    setMapDestination(null);
    setMapVia(null);
    setTolls([]);
    setFuels([]);
  };

  useEffect(() => {
    let cancelled = false;
    clearMap();
    setData(null);
    setUnavailable(false);
    const { originCity, destinationCity, viaCities } = getRequestDataV2(
      {
        origin: rawOrigin,
        destination: rawDestination,
        truckLocation: truckLocation,
      },
      isBroker
    );

    const isValid = !!rawOrigin && !!rawDestination;
    if (!isValid) return;

    const tokens = {
      accessToken: accessToken || "",
      refreshToken: refreshToken || "",
    };

    const fetchRoute = async () => {
      dispatch(setLoading(true));
      try {
        const withDh = [
          originCity,
          ...(viaCities || []).filter(Boolean),
          destinationCity,
        ].filter(Boolean);
        const withoutDh = (
          isBroker
            ? [originCity, destinationCity]
            : [(viaCities || []).filter(Boolean)[0] || originCity, destinationCity]
        ).filter(Boolean);

        let gh =
          USE_GRAPHHOPPER && accessToken
            ? await fetchGhRoute(withDh, tokens)
            : null;
        if (!gh && accessToken && withDh.length > 2) {
          gh = await fetchGhRoute(withoutDh, tokens);
        }
        if (cancelled) return;
        if (!gh) {
          setUnavailable(true);
          dispatch(
            setDistance({
              kilometersRaw: 0,
              kilometers: "0 km",
              milesRaw: 0,
              miles: "0 mi",
            })
          );
          dispatch(setDuration("0"));
          dispatch(setTollCost({ usd: "0", cad: "0" }));
          return;
        }
        const route = gh;
        setData(toRouting8(route));
        pollTolls(route.sid, tokens).then((t) => {
          if (!cancelled) setData(toRouting8(route, t));
        });
      } catch (err) {
        console.error("Failed to fetch route", err);
        if (!cancelled) setUnavailable(true);
      } finally {
        if (!cancelled) dispatch(setLoading(false));
      }
    };

    fetchRoute();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, rawOrigin, rawDestination, truckLocation, isBroker, accessToken, refreshToken]);

  useEffect(() => {
    if (data) {
      handleResponse(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrack, data]);

  const handleResponse = (data: any) => {
    if (!data?.routes?.[0]?.sections?.[0]) return;
    const loadTripData =
      data.routes[0].sections.length > 1
        ? data.routes[0].sections[1]
        : data.routes[0].sections[0];
    const deadHeadTripData =
      data.routes[0].sections.length > 1 && hasTrack
        ? data.routes[0].sections[0]
        : null;

    setPolylinePoints([
      ...sectionCoords(deadHeadTripData),
      ...sectionCoords(loadTripData),
    ]);
    setMapOrigin(
      deadHeadTripData?.departure.place.location ||
      loadTripData.departure.place.location
    );
    setMapVia(deadHeadTripData ? loadTripData.departure.place.location : null);
    setMapDestination(loadTripData.arrival.place.location);

    const tolls = [
      ...(deadHeadTripData?.tolls || []),
      ...(loadTripData.tolls || []),
    ]
      .filter((toll: any) => !!toll.tollCollectionLocations && +toll.price > 0)
      .map((toll: any) => ({
        location: toll.tollCollectionLocations[0].location,
        name: toll.tollCollectionLocations[0].name,
        price: +toll.price,
      }));
    setTolls(tolls || []);
    setFuels(
      (data._fuels || []).map((f: any) => ({
        location: { lat: f.lat, lng: f.lng },
        name: f.name,
        price: +f.price,
      }))
    );

    const metersRaw =
      +(deadHeadTripData?.travelSummary.length || 0) +
      +loadTripData.travelSummary.length;
    const duration =
      +(deadHeadTripData?.travelSummary.duration || 0) +
      +loadTripData.travelSummary.duration;
    const tollCost =
      +(deadHeadTripData?.travelSummary.tolls?.total?.value || 0) +
      +(loadTripData.travelSummary.tolls?.total?.value || 0);
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);

    const durationString = `${hours} h ${minutes} m`;

    const kilometersRaw = Math.round(metersRaw / 1000);
    const milesRaw = Math.round(metersRaw * 0.000621371);

    dispatch(
      setDistance({
        kilometersRaw,
        kilometers: kilometersRaw + " km",
        milesRaw,
        miles: milesRaw + " mi",
      })
    );
    dispatch(setDuration(durationString));
    dispatch(
      setTollCost({
        usd: tollCost.toFixed(2),
        cad: (tollCost * 1.36).toFixed(2),
      })
    );
    const diesel = dieselFromFuel(data._fuel, rawOrigin || "");
    if (diesel) dispatch(setFuelPrice(diesel));
    if (hasTrack && !distanceDifference.miles) {
      const diff = +deadHeadTripData?.travelSummary.length || 0;
      dispatch(
        setDistanceDifference({
          kilometers: Math.round(diff / 1000),
          miles: Math.round(diff * 0.000621371),
        })
      );
    }
  };

  const allPoints: LatLng[] = [
    ...(mapOrigin ? [mapOrigin] : []),
    ...(mapVia ? [mapVia] : []),
    ...(mapDestination ? [mapDestination] : []),
  ];

  return (
    <>
      <Loader show={loading} />
      {unavailable && (
        <div className="absolute inset-0 z-[1] flex items-center justify-center">
          <p className="text-white font-semibold">Route unavailable</p>
        </div>
      )}
      <MapContainer
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        zoom={6}
        center={mapOrigin || { lat: 39.5, lng: -98.35 }} // fallback center
        scrollWheelZoom
        zoomControl={false}
        minZoom={3}
        maxZoom={15}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={allPoints} />

        {polylinePoints.length > 0 && (
          <Polyline positions={polylinePoints} color="blue" />
        )}

        {mapOrigin && <Marker position={mapOrigin} icon={startIcon} />}
        {mapVia && <Marker position={mapVia} icon={loadIcon} />}
        {mapDestination && (
          <Marker position={mapDestination} icon={pointIcon} />
        )}

        {!isBroker &&
          tolls.map((toll, index) => (
            <Marker
              key={`toll-${index}-${toll.name}`}
              position={toll.location}
              icon={priceIcon("lc-map-toll", toll.price.toFixed(2))}
            >
              <Popup>
                {toll.name || "Toll"} ${toll.price.toFixed(2)}
              </Popup>
            </Marker>
          ))}
        {fuels.map((fuel, index) => (
          <Marker
            key={`fuel-${index}-${fuel.name}`}
            position={fuel.location}
            icon={priceIcon("lc-map-fuel", fuel.price.toFixed(2))}
          >
            <Popup>
              {fuel.name || "Fuel"} ${fuel.price.toFixed(2)}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </>
  );
};
